/**
 * Production wallet provider — bridges Privy + wagmi into the WalletContext.
 *
 * This file is the ONLY place that imports from @privy-io/react-auth and wagmi hooks.
 * It is only rendered when PrivyProvider + WagmiProvider are in the component tree.
 */

import { usePrivy } from "@privy-io/react-auth";
import { type ReactNode, useCallback, useMemo } from "react";
import { useAccount, useDisconnect } from "wagmi";
import { WalletContext, type WalletContextValue } from "./use-wallet";

export function PrivyWalletProvider({ children }: { children: ReactNode }) {
	const { login: privyLogin, logout, authenticated, getAccessToken } = usePrivy();
	const { address, isConnected, chain } = useAccount();
	const { disconnect } = useDisconnect();

	const handleLogin = useCallback(async () => {
		if (authenticated) {
			// Privy session persisted but wallet not connected — clear stale session first
			await logout();
		}
		privyLogin();
	}, [authenticated, privyLogin, logout]);

	const handleDisconnect = useCallback(() => {
		disconnect();
		logout();
	}, [disconnect, logout]);

	const value = useMemo<WalletContextValue>(
		() => ({
			isConnected: authenticated && isConnected,
			address,
			chainId: chain?.id,
			login: handleLogin,
			logout,
			disconnect: handleDisconnect,
			getAccessToken,
		}),
		[
			authenticated,
			isConnected,
			address,
			chain?.id,
			handleLogin,
			logout,
			handleDisconnect,
			getAccessToken,
		]
	);

	return <WalletContext.Provider value={value}>{children}</WalletContext.Provider>;
}
